import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { AuthRequest } from "../models/auth-request";
import { AuthResponse, CurrentUser, IUser, RefreshResponse } from "../models/auth-response";
import { LocalStorageService } from "src/app/shared/localstorage.service";
import { APIResponse } from "src/app/core/models/api-response";
import { MfaChallenge, MfaEmailSendResult } from "../models/mfa-challenge";

@Injectable({
    providedIn: 'root',
})
export class AuthService {

    constructor(private http: HttpClient) { }

    /**
     * Answers with a session or an MFA challenge, both as HTTP 200. Callers
     * must discriminate on `mfa_required` before reading `access_token` --
     * see `isMfaChallenge`.
     */
    login(payload: AuthRequest): Observable<APIResponse<AuthResponse | MfaChallenge>> {
        const httpOptions = {
            headers: { 'Content-Type': 'application/json' },
            params: new HttpParams().set('database', payload?.database || ''),
        };
        return this.http.post<APIResponse<AuthResponse | MfaChallenge>>(
            environment.BASE_URL_API + 'auth/token',
            {
                email: payload.email,
                password: payload.password
            },
            httpOptions,
        );
    }
    
    /**
     * Completes a login with the second factor. Returns the ordinary login
     * body, with permissions recomputed from the user rather than carried
     * across from the challenge.
     *
     * `mfaToken` goes in the body, never as a Bearer: it is a
     * half-authenticated credential the API refuses everywhere else.
     */
    verifyMfa(mfaToken: string, code: string): Observable<APIResponse<AuthResponse>> {
        return this.http.post<APIResponse<AuthResponse>>(
            `${environment.BASE_URL_API}auth/mfa/verify`,
            { mfa_token: mfaToken, code }
        );
    }

    /**
     * Sends a one-time code to the address on the account answering this
     * challenge. Distinct from `auth/mfa/email/send`, which is the
     * authenticated enrolment route and refuses login codes.
     */
    sendChallengeEmailCode(mfaToken: string): Observable<APIResponse<MfaEmailSendResult>> {
        return this.http.post<APIResponse<MfaEmailSendResult>>(
            `${environment.BASE_URL_API}auth/mfa/challenge/email`,
            { mfa_token: mfaToken }
        );
    }

    getCurrentUser(): IUser | null {
        const currentUser = LocalStorageService.get('currentUser');
        return currentUser?.user || null;
    }

    getAuthStatus(): boolean {
        try {
            const currentUser = LocalStorageService.get('currentUser');
            return !!currentUser.access_token;
        } catch {
            return false;
        }
    }

    /**
     * Exchange a refresh token for a new access token.
     *
     * Rotated server-side: the token sent is consumed and a replacement comes
     * back. Callers MUST store it - replaying a consumed token revokes the
     * session.
     */
    refreshAccessToken(refreshToken: string): Observable<APIResponse<RefreshResponse>> {
        return this.http.post<APIResponse<RefreshResponse>>(
            `${environment.BASE_URL_API}auth/refresh/token`,
            { refresh_token: refreshToken }
        );
    }

    /**
     * Revoke the session behind *refreshToken* server-side.
     *
     * Always 200, even for an unknown token, so it cannot be used to probe
     * whether a stolen token is still live.
     */
    logout(refreshToken: string): Observable<APIResponse<void>> {
        return this.http.post<APIResponse<void>>(
            `${environment.BASE_URL_API}auth/logout`,
            { refresh_token: refreshToken }
        );
    }

    getMyPermissions(): Observable<APIResponse<string>> {
        return this.http.get<APIResponse<string>>(
            `${environment.BASE_URL_API}auth/me/permissions`
        );
    }

    public forgetPassword(
        payload: { email: string, database?: string }
    ): Observable<APIResponse<void>> {
        return this.http.post<APIResponse<void>>(
            `${environment.BASE_URL_API}auth/forgot-password`,
            { email: payload.email },
            {
                headers: {
                    'Content-Type': 'application/json'
                },
                params: new HttpParams().set('database', payload?.database || ''),
            }
        );
    }

    resetPassword(newPassword: string, confirmPassword: string, token: string): Observable<APIResponse<void>> {
        const body = {
            newPassword: newPassword,
            confirmPassword: confirmPassword,
            token: token
        };

        return this.http.post<APIResponse<void>>(
            `${environment.BASE_URL_API}auth/reset-password`,
            body,
            {
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        );
    }

    getDBs(): Observable<APIResponse<string>> {
        return this.http.get<APIResponse<string>>(`${environment.BASE_URL_API}databases`)
    }
}
